import { useTranslation } from 'react-i18next';

import { toGrams, WEIGHT_UNITS, type WeightUnitCode } from '@nucleus-commerce/core';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import type { WeightFieldInput } from '../schemas/weight-schema';

const DECIMAL = /^\d+(\.\d+)?$/;

interface WeightInputProps {
  value: WeightFieldInput;
  onChange: (next: WeightFieldInput) => void;
  onBlur?: () => void;
  /** Shown while the field has no unit of its own; written into the field once a value is typed. */
  defaultUnit: WeightUnitCode;
  disabled?: boolean;
  id?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  /** Name of the number field when no <label> points at it (a table cell). */
  'aria-label'?: string;
  /** A table cell: small fields, a narrow unit picker and no gram line under them. */
  compact?: boolean;
}

/**
 * A weight as staff enter it: a number and a unit, kept as entered by the backend (D-141). The
 * gram figure underneath is the one shipping rates use.
 */
export function WeightInput({
  value,
  onChange,
  onBlur,
  defaultUnit,
  disabled,
  id,
  'aria-invalid': invalid,
  'aria-describedby': describedBy,
  'aria-label': ariaLabel,
  compact = false,
}: WeightInputProps) {
  const { t } = useTranslation(['products', 'common']);
  const format = useFormat();
  const unit = value.unit || defaultUnit;
  const text = value.value.trim();
  const grams = DECIMAL.test(text) ? toGrams(Number(text), unit) : null;

  return (
    <div className="flex flex-col gap-1.5">
      <div className={cn('flex', compact ? 'gap-1' : 'gap-2')}>
        <Input
          id={id}
          inputMode="decimal"
          value={value.value}
          onChange={(event) => onChange({ value: event.target.value, unit })}
          onBlur={onBlur}
          disabled={disabled}
          aria-invalid={invalid}
          aria-describedby={describedBy}
          aria-label={ariaLabel}
          className={cn('min-w-0 flex-1', compact && 'h-8 text-xs tabular-nums')}
        />
        <Select
          value={unit}
          onValueChange={(next) => onChange({ value: value.value, unit: next as WeightUnitCode })}
          disabled={disabled}
        >
          <SelectTrigger
            aria-label={t('products:weight.unit')}
            size={compact ? 'sm' : 'default'}
            className={cn('shrink-0', compact ? 'w-28 text-xs' : 'w-44')}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WEIGHT_UNITS.map((code) => (
              <SelectItem key={code} value={code}>
                {t(`common:weightUnits.${code}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {grams !== null && !compact ? (
        <p className="text-muted-foreground text-xs tabular-nums" data-testid="weight-grams">
          {t('products:weight.inGrams', {
            grams: format.number(grams, { minimumFractionDigits: 3, maximumFractionDigits: 3 }),
          })}
        </p>
      ) : null}
    </div>
  );
}
